/**
 * Cluster IDs whose portal entry points live on another site.
 *
 * Cluster slugs are not stable, so the match is made on the decoded cluster
 * ID. Only the cluster root (`/:clusterSlug`) and its login page
 * (`/:clusterSlug/login`) are redirected; every other route for the cluster
 * keeps working here. Add future clusters to this map.
 */
export const EXTERNAL_CLUSTER_REDIRECTS: Readonly<Record<string, string>> = {};

export const getExternalRedirectForClusterId = (
  clusterId: string | null | undefined,
  currentHostname: string = typeof window === "undefined"
    ? ""
    : window.location.hostname
): string | null => {
  const redirectUrl = (clusterId && EXTERNAL_CLUSTER_REDIRECTS[clusterId]) || null;

  // The target is a legacy cluster URL that this app would canonicalise back
  // to `/ci_...`, so never redirect when already on the target host.
  if (!redirectUrl || new URL(redirectUrl).hostname === currentHostname) {
    return null;
  }

  return redirectUrl;
};

const REDIRECTED_CLUSTER_ROUTES = new Set(["", "login"]);

type DecodeClusterSlug = (clusterSlug: string) => string | null;

export const getExternalClusterRedirect = (
  pathname: string,
  decodeClusterSlug: DecodeClusterSlug
): string | null => {
  const segments = pathname.split("/").filter(Boolean);
  const clusterSlug = segments[0];

  if (
    !clusterSlug?.startsWith("ci_") ||
    segments.length > 2 ||
    !REDIRECTED_CLUSTER_ROUTES.has(segments[1] || "")
  ) {
    return null;
  }

  return getExternalRedirectForClusterId(decodeClusterSlug(clusterSlug));
};
